import {ContinueButton} from "@nolimitcity/slot-launcher/bin/gui/buttons/concretebuttons/ContinueButton";
import {HOTB} from "../HOTB";
import {ScreenBounds} from "@nolimitcity/slot-launcher/bin/display/ScreenBounds";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {gsap} from "gsap";
import {HOTBData} from "../server/HOTBData";
import {GameUtils} from "../utils/GameUtils";
import {GameAssets} from "../GameAssets";
import {ExtendedGameModel} from "../server/ExtendedGameModel";
import {ExtendedGameMode} from "../server/ExtendedGameMode";
import {GameSounds} from "../sound/GameSounds";
import {SimplePresentationRunnerController} from "../../core/presentation/SimplePresentationRunner";

export class FSIntroView extends PIXI.Container implements StageLayer {
    private _upgradeImage: PIXI.Sprite;
    private _gameTitleImage: PIXI.Sprite;
    private _spinsCountText: PIXI.BitmapText;
    private _fontStyle: any = {
        fontName: GameAssets.BITMAP_FONT_FS_INTRO_OUTRO,
        fontSize: 26
    };
    private _button: ContinueButton;
    private _clickCallback: () => void;
    private _introController: SimplePresentationRunnerController | undefined;

    constructor() {
        super();
        this.name = "FSIntroView";
        this.init();
        this.onResize();
    }

    public onResize(): void {
        const bounds: ScreenBounds = NolimitApplication.screenBounds;
        if (NolimitApplication.isLandscape) {
            this._button.position.set(360, 615);
        } else {
            const centerBottom: number = (bounds.bottom - 610) * 0.5 + 610;
            this._button.position.set(360, centerBottom - 80);
        }
    }

    public showIntro(callback: () => void, controller?: SimplePresentationRunnerController): void {
        if (controller) {
            this._introController = controller;
        }
        const tl: gsap.core.Timeline = gsap.timeline(),
            gameModel: ExtendedGameModel = HOTB.model,
            data: HOTBData = gameModel.data,
            isRestore: boolean = gameModel.isRestore,
            gameTypePrefix: string = GameUtils.getGamePrefix(data.nextMode);

        this.updateDynamicContent(gameTypePrefix, data.freespinsLeft);

        tl.add((): void => {
            if (!isRestore) {
                HOTB.soundPlayer.fadeMusic(0, 100);
            }

            if (data.nextMode === ExtendedGameMode.FREESPIN) {
                HOTB.soundPlayer.playLoopEffect(GameSounds.FS_INTRO_LOOP);
            } else {
                HOTB.soundPlayer.playLoopEffect(GameSounds.SFS_INTRO_LOOP);
            }
        });

        if (isRestore) {
            tl.add(HOTB.gameForeground.handleDoors(false, false));
        } else {
            tl.add(HOTB.gameForeground.handleDoors(false, true));

            tl.add((): void => {
                if (this._introController) {
                    const skipLabelTime: number = tl.labels["skipTransition"] || 0;

                    if (tl.time() < skipLabelTime) {
                        this._introController.setSkipCallback((): void => {
                            const currTransition: string = HOTB.gameBackground.bgTransitionName,
                                fromMode: string = currTransition.split("_to_")[0],
                                toMode: string = currTransition.split("_to_")[1],
                                soundToStop: string = (toMode === "fs") ? GameSounds.MOVE_MAIN_FS : (fromMode === "fs") ? GameSounds.MOVE_FS_SFS : GameSounds.MOVE_MAIN_SFS,
                                skipSoundToPlay: string = (toMode === "fs") ? GameSounds.MOVE_SKIP_FS : GameSounds.MOVE_SKIP_SFS;

                            HOTB.soundPlayer.stopSound(soundToStop);
                            HOTB.soundPlayer.playSound(skipSoundToPlay);

                            tl.seek("skipTransition", false);
                        });
                    }
                }
            });

            tl.add(HOTB.gameBackground.playBGTransition());
        }

        tl.add((): void => {
            if (this._introController) {
                this._introController.setSkipCallback();
            }
        });

        tl.addLabel("skipTransition");

        let isUpgrade: boolean = false;

        tl.add((): void => {
            this._clickCallback = callback;

            this._button.alpha = 0;
            this._gameTitleImage.alpha = 0;
            this._spinsCountText.alpha = 0;

            this._gameTitleImage.visible = true;
            this._spinsCountText.visible = true;

            this._upgradeImage.alpha = 0;

            if (gameModel.gameData?.fsUpgradeToSuper) {
                isUpgrade = true;
            }

            if (gameModel.isRestore && (gameModel.data.mode === ExtendedGameMode.FREESPIN ||
                    gameModel.data.mode === ExtendedGameMode.FREESPIN_AVALANCHE) &&
                gameModel.data.nextMode === ExtendedGameMode.FREESPIN_SUPER) {
                isUpgrade = true;
            }

            this._button.visible = !isUpgrade;
            this._upgradeImage.visible = isUpgrade;
        });

        tl.to([this._button, this._upgradeImage, this._gameTitleImage, this._spinsCountText], {
            duration: 0.5,
            alpha: 1
        });

        tl.add((): void => {
            this._button.enable(true);
        });

        tl.add((): void => {
            if (isUpgrade) {
                gsap.to(this, {
                    duration: 2,
                    onComplete: (): void => {
                        this.onButtonClick();
                    }
                });
            }
        });
    }

    private updateDynamicContent(gamePrefix: string, spinsCount: number): void {
        this._gameTitleImage.texture = HOTB.assetLoader.getTextures(gamePrefix + "IntroText")[0];

        if (spinsCount > 0) {
            this._spinsCountText.text = spinsCount + "";
        } else {
            this._spinsCountText.text = "";
        }
    }

    private init(): void {
        this.createUpgradeImage();
        this.createTitleImage();
        this.createSpinsCountText();
        this.createButton();
    }

    private createUpgradeImage(): void {
        this._upgradeImage = new PIXI.Sprite(HOTB.assetLoader.getTextures("upgradeText")[0]);
        this._upgradeImage.anchor.set(0.5);
        this._upgradeImage.position.set(360, 310);
        this._upgradeImage.visible = false;
        this.addChild(this._upgradeImage);
    }

    private createTitleImage(): void {
        this._gameTitleImage = new PIXI.Sprite(HOTB.assetLoader.getTextures("fsIntroText")[0]);
        this._gameTitleImage.anchor.set(0.5);
        this._gameTitleImage.scale.set(0.9);
        this._gameTitleImage.position.set(360, 490);
        this._gameTitleImage.visible = false;
        this.addChild(this._gameTitleImage);
    }

    private createSpinsCountText(): void {
        this._spinsCountText = new PIXI.BitmapText("", this._fontStyle);
        this._spinsCountText.anchor = new PIXI.Point(0.5, 0.5);
        this._spinsCountText.position.set(360, 360);
        this._spinsCountText.visible = false;

        this.addChild(this._spinsCountText);
    }

    private createButton(): void {
        this._button = new ContinueButton();
        this._button.addClickCallback((): void => {
            this.onButtonClick();
        });
        this._button.pivot.set(this._button.width * 0.5, this._button.height * 0.5);
        this._button.enable(false)
        this._button.visible = false;

        this.addChild(this._button);
    }

    private onButtonClick(): void {
        HOTB.model.isActionSpinToBonus = false;
        HOTB.soundPlayer.playSound(GameSounds.CONTINUE_BUTTON);
        this._button.enable(false);
        HOTB.model.isPlayingBonusSpins = true;
        const tl: gsap.core.Timeline = gsap.timeline(),
            gameModel: ExtendedGameModel = HOTB.model,
            isRestore: boolean = gameModel.isRestore,
            data: HOTBData = gameModel.data,
            fsLeftCount: number = data.freespinsLeft,
            duration: number = 0.5;

        tl.add([
            HOTB.gameForeground.handleDoors(true, true),
            (): void => {
                const nextMode: ExtendedGameMode = data.nextMode;

                HOTB.soundPlayer.switchMusic(nextMode);

                if (nextMode === ExtendedGameMode.FREESPIN) {
                    HOTB.soundPlayer.stopLoopEffect(GameSounds.FS_INTRO_LOOP);
                } else {
                    HOTB.soundPlayer.stopLoopEffect(GameSounds.SFS_INTRO_LOOP);
                }
            }
        ]);

        tl.add([
            (): void => {
                if (isRestore) {
                    HOTB.keypadPlugin.setZeroBetSpinCounter(Math.max(fsLeftCount, 0));
                } else {
                    HOTB.keypadPlugin.setZeroBetSpinCounter(fsLeftCount);
                }
            },
        ], 0);

        tl.to([this._button, this._upgradeImage, this._gameTitleImage, this._spinsCountText], {
            duration: duration,
            alpha: 0
        }, 0);

        tl.add((): void => {
            this._button.visible = false;
            this._upgradeImage.visible = false;
            this._gameTitleImage.visible = false;
            this._spinsCountText.visible = false;
            this._introController = undefined;
            this._clickCallback();
        });
    }
}